import React, { createContext, useContext } from "react";
import { useLocalObservable } from "mobx-react";
import { AgentChatStore, ScopedAgentChatStoreRegistry } from "./agentChatStore";
import { DEFAULT_SCOPE, Scope } from "../common";

// ─── Registry Context (app-level, single instance) ───────────────────────────

export const AgentChatContext = createContext<ScopedAgentChatStoreRegistry | null>(null);
AgentChatContext.displayName = "AgentChatContext";

export const AgentChatProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const registry = useLocalObservable(() => new ScopedAgentChatStoreRegistry());
  return <AgentChatContext.Provider value={registry}>{children}</AgentChatContext.Provider>;
};

// ─── Scope Context (feature-level, declares active chat scope) ───────────────

const ChatScopeContext = createContext<Scope | null>(null);
ChatScopeContext.displayName = "ChatScopeContext";

/**
 * Provides a chat scope to all descendants.
 * Wrap a feature boundary (e.g. drawer) once; child hooks read via useChatScope().
 */
export const ChatScopeProvider: React.FC<React.PropsWithChildren<{ scope: Scope }>> = ({
  scope,
  children,
}) => <ChatScopeContext.Provider value={scope}>{children}</ChatScopeContext.Provider>;

/**
 * Read the active chat scope from context.
 * Must be used within a ChatScopeProvider boundary.
 */
export function useChatScope(): Scope {
  const scope = useContext(ChatScopeContext);
  return scope ?? DEFAULT_SCOPE;
}
